"use client";

import { useState, useEffect } from "react";
import {
    Button,
    Table,
    Tag,
    Drawer,
    Form,
    Input,
    Spin,
    message,
    Switch,
    Space,
    Popconfirm,
    InputNumber,
    Select,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import useQuotesStore, { Quote } from "./QuotesStore.ts";

const { TextArea } = Input;
const { Option } = Select;

export default function Quotes() {
    const {
        quotes,
        loading,
        pagination,
        fetchQuotes,
        createQuote,
        updateQuote,
        deleteQuote,
        toggleQuote,
    } = useQuotesStore();

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(30);
    const [form] = Form.useForm();
    const [editingId, setEditingId] = useState<number | null>(null);

    useEffect(() => {
        fetchQuotes({ page: currentPage, per_page: pageSize });
    }, [currentPage, pageSize]);

    const handleTableChange = (pagination: TablePaginationConfig) => {
        setCurrentPage(pagination.current || 1);
        setPageSize(pagination.pageSize || 30);
    };

    const handleAddQuote = () => {
        setIsEditMode(false);
        setEditingId(null);
        form.resetFields();
        setIsDrawerOpen(true);
    };

    const handleEditQuote = (quote: Quote) => {
        setIsEditMode(true);
        setEditingId(quote.id);
        form.resetFields();
        form.setFieldsValue({
            content: quote.content,
            type: quote.type,
            language: quote.language,
            priority: quote.priority,
            is_active: quote.is_active,
        });
        setIsDrawerOpen(true);
    };

    const handleDeleteQuote = async (id: number) => {
        try {
            await deleteQuote(id);
            message.success("Quote deleted successfully");
        } catch (error) {
            message.error("Failed to delete quote");
        }
    };

    const handleToggleQuote = async (id: number) => {
        try {
            await toggleQuote(id);
            message.success("Quote status updated");
        } catch (error) {
            message.error("Failed to update quote status");
        }
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            if (isEditMode && editingId) {
                await updateQuote(editingId, values);
                message.success("Quote updated successfully");
            } else {
                await createQuote(values);
                message.success("Quote created successfully");
            }

            setIsDrawerOpen(false);
            form.resetFields();
        } catch (error: any) {
            message.error(error.message || "Failed to save quote");
        }
    };

    const columns: ColumnsType<Quote> = [
        {
            title: <span className="font-semibold">Content</span>,
            dataIndex: "content",
            key: "content",
            ellipsis: true,
            render: (text) => (
                <div className="max-w-md">
                    <p className="text-sm text-gray-900">{text}</p>
                </div>
            ),
        },
        {
            title: <span className="font-semibold">Type</span>,
            dataIndex: "type",
            key: "type",
            render: (type) => <Tag color="blue">{type || "N/A"}</Tag>,
        },
        {
            title: <span className="font-semibold">Language</span>,
            dataIndex: "language",
            key: "language",
            render: (lang) => <Tag color="purple">{lang || "N/A"}</Tag>,
        },
        {
            title: <span className="font-semibold">Priority</span>,
            dataIndex: "priority",
            key: "priority",
            align: "center",
            render: (priority) => (
                <Tag color={priority > 5 ? "red" : priority > 3 ? "orange" : "green"}>
                    {priority}
                </Tag>
            ),
        },
        {
            title: <span className="font-semibold">Status</span>,
            dataIndex: "is_active",
            key: "is_active",
            render: (isActive, record) => (
                <Switch
                    checked={isActive}
                    onChange={() => handleToggleQuote(record.id)}
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                />
            ),
        },
        {
            title: <span className="font-semibold">Actions</span>,
            key: "action",
            align: "center",
            width: 160,
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<i className="fa-solid fa-pen-to-square"></i>}
                        type="text"
                        className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => handleEditQuote(record)}
                    />
                    <Popconfirm
                        title="Delete this quote?"
                        description="Are you sure to delete this quote?"
                        onConfirm={() => handleDeleteQuote(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            icon={<i className="fa-solid fa-trash"></i>}
                            type="text"
                            danger
                            className="hover:bg-red-50"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const paginationConfig = {
        current: pagination.current_page || 1,
        pageSize: pagination.per_page || 30,
        total: pagination.total || 0,
        showSizeChanger: true,
        showTotal: (total: number, range: [number, number]) =>
            `${range[0]}-${range[1]} of ${total} quotes`,
    };

    return (
        <>
            <div className="mb-8">
                <div className="flex items-center gap-2 text-sm text-gray-400 mb-2 font-medium">
                    <span>Home</span>
                    <span>/</span>
                    <span className="text-black">Quotes</span>
                </div>
                <h1 className="text-3xl font-bold text-black">Quotes Management</h1>
            </div>

            <div className="bg-white rounded-3xl shadow-soft border border-gray-100 overflow-hidden">
                <div className="p-6">
                    <div className="mb-6 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center text-primary-600">
                                <i className="fa-solid fa-quote-left text-black text-lg"></i>
                            </div>
                            <h3 className="text-lg font-bold text-black m-0">All Quotes</h3>
                        </div>
                        <Button
                            type="primary"
                            icon={<i className="fa-solid fa-plus text-black"></i>}
                            className="shadow-lg shadow-yellow-200/50"
                            onClick={handleAddQuote}
                        >
                            Add New Quote
                        </Button>
                    </div>
                    <Table
                        columns={columns}
                        dataSource={quotes}
                        loading={loading}
                        rowKey="id"
                        pagination={paginationConfig}
                        onChange={handleTableChange}
                    />
                </div>
            </div>

            <Drawer
                title={
                    <span className="flex items-center gap-2 text-lg">
                        <i className="fa-solid fa-quote-left text-black"></i>
                        {isEditMode ? "Edit Quote" : "New Quote"}
                    </span>
                }
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                width={600}
                destroyOnClose
                footer={
                    <div className="flex justify-end gap-2">
                        <Button onClick={() => setIsDrawerOpen(false)}>Cancel</Button>
                        <Button type="primary" onClick={handleSave} loading={loading}>
                            Save
                        </Button>
                    </div>
                }
            >
                <Form form={form} layout="vertical" className="pt-4">
                    <Form.Item
                        name="content"
                        label="Content"
                        rules={[{ required: true, message: "Please enter quote content" }]}
                    >
                        <TextArea rows={4} placeholder="Enter quote content..." />
                    </Form.Item>

                    <div className="grid grid-cols-2 gap-4">
                        <Form.Item
                            name="type"
                            label="Type"
                            rules={[{ required: true, message: "Please select type" }]}
                        >
                            <Select placeholder="Select type">
                                <Option value="motivational">Motivational</Option>
                                <Option value="inspirational">Inspirational</Option>
                                <Option value="funny">Funny</Option>
                                <Option value="wisdom">Wisdom</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="language"
                            label="Language"
                            rules={[{ required: true, message: "Please select language" }]}
                        >
                            <Select placeholder="Select language">
                                <Option value="en">English</Option>
                                <Option value="vi">Vietnamese</Option>
                            </Select>
                        </Form.Item>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <Form.Item
                            name="priority"
                            label="Priority"
                            initialValue={1}
                            rules={[{ required: true, message: "Please enter priority" }]}
                        >
                            <InputNumber min={1} max={10} className="w-full" placeholder="1-10" />
                        </Form.Item>

                        <Form.Item
                            name="is_active"
                            label="Status"
                            valuePropName="checked"
                            initialValue={true}
                        >
                            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                        </Form.Item>
                    </div>
                </Form>
            </Drawer>
        </>
    );
}





